/**
 * 矩形プレーン OBB、多角形の三角柱、ブレースのカプセルを GPU へ載せる。
 * 厚み 0 のプレーンも薄い板としてボクセル SDF と min する。
 */

import * as THREE from 'three';
import {
  ANALYTIC_ACCEL_HEIGHT,
  ANALYTIC_ACCEL_WIDTH,
  ANALYTIC_BRACE_CAPACITY,
  ANALYTIC_PLANE_CAPACITY,
  ANALYTIC_TRIANGLE_CAPACITY,
  buildAnalyticBvh,
  collectAnalyticSdf,
  packAnalyticAccelMap,
  packAnalyticBraces,
  packAnalyticPlanes,
  packAnalyticTriangles,
  type AnalyticSdfLeftover,
} from '../shared/analytic-sdf';
import type { Entity } from '../shared/types';
import type { VoxelVolume } from '../shared/voxel-sdf';
import { volumesEqual } from '../shared/voxel-sdf';

export class AnalyticSdfGpu {
  planes: THREE.DataTexture;
  triangles: THREE.DataTexture;
  braces: THREE.DataTexture;
  originMap: THREE.DataTexture;
  planeCount = 0;
  triangleCount = 0;
  braceCount = 0;
  packedIds: string[] = [];
  leftover: AnalyticSdfLeftover = { planes: [], triangles: [], braces: [], ids: [] };
  private planeData: Float32Array;
  private triangleData: Float32Array;
  private braceData: Float32Array;
  private originData: Float32Array;
  private volume: VoxelVolume | null = null;
  private revision = -1;
  private skipKey = '';

  constructor() {
    this.planeData = packAnalyticPlanes([]);
    this.planes = makePlaneTexture(this.planeData);
    this.triangleData = packAnalyticTriangles([]);
    this.triangles = makeTriangleTexture(this.triangleData);
    this.braceData = packAnalyticBraces([]);
    this.braces = makeBraceTexture(this.braceData);
    this.originData = packAnalyticAccelMap(buildAnalyticBvh([], [], []));
    this.originMap = makeAccelTexture(this.originData);
  }

  rebuild(
    entities: Iterable<Entity>,
    skipIds: ReadonlySet<string>,
    volume: VoxelVolume,
    revision: number
  ): string[] {
    const skipKey = skipSignature(skipIds);
    const originChanged = !this.volume || !volumesEqual(this.volume, volume);
    if (!originChanged && revision === this.revision && skipKey === this.skipKey && this.revision >= 0) {
      return this.packedIds;
    }

    const list = Array.from(entities);
    const { planes: planeList, triangles: triangleList, braces: braceList, packedIds, leftover } =
      collectAnalyticSdf(list, skipIds, volume);
    this.packedIds = packedIds;
    this.leftover = leftover;
    this.planeData = packAnalyticPlanes(planeList);
    const image = this.planes.image as unknown as { data: Float32Array };
    image.data.set(this.planeData);
    this.planes.needsUpdate = true;
    this.planeCount = planeList.length;
    this.triangleData = packAnalyticTriangles(triangleList);
    const triangleImage = this.triangles.image as unknown as { data: Float32Array };
    triangleImage.data.set(this.triangleData);
    this.triangles.needsUpdate = true;
    this.triangleCount = triangleList.length;
    this.braceData = packAnalyticBraces(braceList);
    const braceImage = this.braces.image as unknown as { data: Float32Array };
    braceImage.data.set(this.braceData);
    this.braces.needsUpdate = true;
    this.braceCount = braceList.length;
    this.originData = packAnalyticAccelMap(buildAnalyticBvh(planeList, triangleList, braceList));
    const accelImage = this.originMap.image as unknown as { data: Float32Array };
    accelImage.data.set(this.originData);
    this.originMap.needsUpdate = true;
    this.volume = {
      origin: { ...volume.origin },
      extent: volume.extent,
      resolution: volume.resolution,
    };
    this.revision = revision;
    this.skipKey = skipKey;
    return this.packedIds;
  }

  invalidate(): void {
    this.revision = -1;
    this.skipKey = '';
    this.volume = null;
    this.packedIds = [];
    this.leftover = { planes: [], triangles: [], braces: [], ids: [] };
  }

  dispose(): void {
    this.planes.dispose();
    this.triangles.dispose();
    this.braces.dispose();
    this.originMap.dispose();
  }
}

function makeAccelTexture(data: Float32Array): THREE.DataTexture {
  const texture = new THREE.DataTexture(
    data as unknown as BufferSource,
    ANALYTIC_ACCEL_WIDTH,
    ANALYTIC_ACCEL_HEIGHT,
    THREE.RGBAFormat,
    THREE.FloatType
  );
  texture.colorSpace = THREE.NoColorSpace;
  texture.minFilter = THREE.NearestFilter;
  texture.magFilter = THREE.NearestFilter;
  texture.generateMipmaps = false;
  texture.flipY = false;
  texture.wrapS = THREE.ClampToEdgeWrapping;
  texture.wrapT = THREE.ClampToEdgeWrapping;
  texture.needsUpdate = true;
  return texture;
}

function makePlaneTexture(data: Float32Array): THREE.DataTexture {
  const texture = new THREE.DataTexture(
    data as unknown as BufferSource,
    ANALYTIC_PLANE_CAPACITY,
    4,
    THREE.RGBAFormat,
    THREE.FloatType
  );
  texture.colorSpace = THREE.NoColorSpace;
  texture.minFilter = THREE.NearestFilter;
  texture.magFilter = THREE.NearestFilter;
  texture.generateMipmaps = false;
  texture.flipY = false;
  texture.wrapS = THREE.ClampToEdgeWrapping;
  texture.wrapT = THREE.ClampToEdgeWrapping;
  texture.needsUpdate = true;
  return texture;
}

function makeTriangleTexture(data: Float32Array): THREE.DataTexture {
  const texture = new THREE.DataTexture(
    data as unknown as BufferSource,
    ANALYTIC_TRIANGLE_CAPACITY,
    3,
    THREE.RGBAFormat,
    THREE.FloatType
  );
  texture.colorSpace = THREE.NoColorSpace;
  texture.minFilter = THREE.NearestFilter;
  texture.magFilter = THREE.NearestFilter;
  texture.generateMipmaps = false;
  texture.flipY = false;
  texture.wrapS = THREE.ClampToEdgeWrapping;
  texture.wrapT = THREE.ClampToEdgeWrapping;
  texture.needsUpdate = true;
  return texture;
}

function makeBraceTexture(data: Float32Array): THREE.DataTexture {
  const texture = new THREE.DataTexture(
    data as unknown as BufferSource,
    ANALYTIC_BRACE_CAPACITY,
    2,
    THREE.RGBAFormat,
    THREE.FloatType
  );
  texture.colorSpace = THREE.NoColorSpace;
  texture.minFilter = THREE.NearestFilter;
  texture.magFilter = THREE.NearestFilter;
  texture.generateMipmaps = false;
  texture.flipY = false;
  texture.wrapS = THREE.ClampToEdgeWrapping;
  texture.wrapT = THREE.ClampToEdgeWrapping;
  texture.needsUpdate = true;
  return texture;
}

function skipSignature(ids: ReadonlySet<string>): string {
  if (ids.size === 0) return '';
  return [...ids].sort().join('\0');
}
